import {
  type BatchCreateItemsApiArg,
  type Item,
  type SavedCypherTree,
  mergeOrderedItems,
  useBatchCreateSavedCypherItemsMutation,
  useCreateSavedCypherTreeMutation,
  useLazyGetSavedCypherTreeByIdQuery,
  useUpdateSavedCypherTreeMutation,
} from '@query/services/api';
import { useCallback } from 'react';

type BatchCreateSavedCypherItemsFn = (arg: BatchCreateItemsApiArg) => Promise<Item[]>;

/**
 * Custom hook that encapsulates the logic to create new saved cypher items in bulk, and update the tree structures'
 * array of ordered items.
 *
 * Items from the payload are mapped to the `clientId` field in order to correlate the items from the payload with
 * items returned from the API. This is to ensure that the newly created items are correctly associated with their
 * parent items in the tree structure.
 *
 * The hook will also ensure that a tree structure document exists, creating one if not.
 *
 * @returns An array of the newly created items.
 */
export const useCommonBatchCreateSavedCypherItemsMutation = () => {
  const [createSavedCypherTree, createSavedCypherTreeRes] = useCreateSavedCypherTreeMutation();
  const [batchCreateSavedCypherItems, batchCreateSavedCypherItemsRes] = useBatchCreateSavedCypherItemsMutation();
  const [updateSavedCypherTree] = useUpdateSavedCypherTreeMutation();
  const [getSavedCypherTreeById] = useLazyGetSavedCypherTreeByIdQuery();

  const batchCreateSavedCypherItemsInternal: BatchCreateSavedCypherItemsFn = useCallback(
    async ({ savedCypherTreeId, items }) => {
      let treeId = savedCypherTreeId;
      let tree: SavedCypherTree | undefined;

      if (treeId === '') {
        const res = await createSavedCypherTree({
          savedCypherTree: {
            orderedItems: [],
          },
        }).unwrap();
        treeId = res.id;
        tree = res;
      } else {
        tree = await getSavedCypherTreeById({ id: treeId }).unwrap();
      }

      const batchCreatedSavedCypherItems = await batchCreateSavedCypherItems({
        savedCypherTreeId: treeId,
        items,
      }).unwrap();

      const orderedItems = mergeOrderedItems(tree.orderedItems, items, batchCreatedSavedCypherItems);

      await updateSavedCypherTree({
        id: treeId,
        savedCypherTree: { orderedItems },
      }).unwrap();

      return batchCreatedSavedCypherItems;
    },
    [createSavedCypherTree, batchCreateSavedCypherItems, getSavedCypherTreeById, updateSavedCypherTree],
  );

  const res = [
    batchCreateSavedCypherItemsInternal,
    {
      isLoading: createSavedCypherTreeRes.isLoading || batchCreateSavedCypherItemsRes.isLoading,
      isError: createSavedCypherTreeRes.isError || batchCreateSavedCypherItemsRes.isError,
      isSuccess: createSavedCypherTreeRes.isSuccess && batchCreateSavedCypherItemsRes.isSuccess,
    },
  ] as const;

  return res;
};
